'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface SignupFormData {
  username: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

export default function SignupForm() {
  const router = useRouter();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>();

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.username });
      toast.success('Akun berhasil dibuat! Selamat bergabung di Nongki 🎉');
      router.push('/');
      router.refresh();
    } catch (error: any) {
      setIsLoading(false);
      setError('email', { message: error?.message || 'Gagal membuat akun, coba lagi' });
    }
  };

  return (
    <div>
      {/* Google Auth */}
      <button className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-border bg-card hover:bg-secondary transition-colors text-sm font-600 text-foreground mb-6 btn-press">
        <span className="text-lg">🇬</span>
        Daftar dengan Google
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-500">atau isi form</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Username */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Username</label>
          <p className="text-xs text-muted-foreground mb-1.5">Ini yang akan ditampilkan di review dan profilmu</p>
          <input
            type="text"
            placeholder="contoh: raraputri_malang"
            className={`input-field ${errors.username ? 'border-red-400' : ''}`}
            {...register('username', {
              required: 'Username wajib diisi',
              minLength: { value: 3, message: 'Username minimal 3 karakter' },
              maxLength: { value: 20, message: 'Username maksimal 20 karakter' },
              pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'Hanya huruf, angka, dan underscore' },
            })}
          />
          {errors.username && <p className="text-xs text-red-500 mt-1.5">{errors.username.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Email</label>
          <input
            type="email"
            placeholder="kamu@email.com"
            className={`input-field ${errors.email ? 'border-red-400' : ''}`}
            {...register('email', {
              required: 'Email wajib diisi',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Format email tidak valid' },
            })}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1.5">{errors.email.message}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Nomor HP <span className="text-muted-foreground font-400">(opsional)</span></label>
          <input
            type="tel"
            placeholder="08xxxxxxxxxx"
            className="input-field"
            {...register('phone')}
          />
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Minimal 8 karakter"
              className={`input-field pr-12 ${errors.password ? 'border-red-400' : ''}`}
              {...register('password', {
                required: 'Password wajib diisi',
                minLength: { value: 8, message: 'Password minimal 8 karakter' },
              })}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500 mt-1.5">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Konfirmasi Password</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="Ulangi password kamu"
              className={`input-field pr-12 ${errors.confirmPassword ? 'border-red-400' : ''}`}
              {...register('confirmPassword', {
                required: 'Konfirmasi password wajib diisi',
                validate: (v) => v === password || 'Password tidak cocok',
              })}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-red-500 mt-1.5">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded accent-accent mt-0.5 flex-shrink-0"
              {...register('agreeTerms', { required: 'Kamu harus menyetujui syarat & ketentuan' })}
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              Saya setuju dengan{' '}
              <button type="button" className="text-accent font-600 hover:underline">Syarat & Ketentuan</button>
              {' '}dan{' '}
              <button type="button" className="text-accent font-600 hover:underline">Kebijakan Privasi</button>
              {' '}Nongki.
            </span>
          </label>
          {errors.agreeTerms && <p className="text-xs text-red-500 mt-1.5">{errors.agreeTerms.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full h-12 text-sm mt-1"
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : 'Buat Akun Gratis'}
        </button>
      </form>
    </div>
  );
}